/**
 * Sums every voice in the meeting into one track for the composite
 * recording: the local microphone (what the recorder says) and each remote
 * participant's audio, plus a share's sound when there is one. Tracks come
 * and go during a meeting; `add` and `remove` follow them. Each source has
 * its own gain so a share's soundtrack can sit under the voices.
 */
export interface Mixer {
  readonly track: MediaStreamTrack
  add(id: string, track: MediaStreamTrack, gain?: number): void
  remove(id: string): void
  stop(): void
}

export function createMixer(): Mixer | null {
  if (typeof AudioContext === 'undefined') return null
  const ctx = new AudioContext()
  const destination = ctx.createMediaStreamDestination()
  const sources = new Map<string, { source: MediaStreamAudioSourceNode; gain: GainNode }>()
  if (ctx.state === 'suspended') void ctx.resume()

  return {
    track: destination.stream.getAudioTracks()[0] as MediaStreamTrack,
    add(id, track, gain = 1) {
      if (sources.has(id) || track.kind !== 'audio') return
      const source = ctx.createMediaStreamSource(new MediaStream([track]))
      const gainNode = ctx.createGain()
      gainNode.gain.value = gain
      source.connect(gainNode).connect(destination)
      sources.set(id, { source, gain: gainNode })
      track.addEventListener('ended', () => this.remove(id), { once: true })
    },
    remove(id) {
      const entry = sources.get(id)
      if (!entry) return
      entry.source.disconnect()
      entry.gain.disconnect()
      sources.delete(id)
    },
    stop() {
      for (const id of [...sources.keys()]) this.remove(id)
      void ctx.close()
    },
  }
}
